import React, { useState, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Avatar } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { ProjectBadge } from '../../components/ui/project-badge';
import TaskTypeIcon from '../../components/shared/TaskTypeIcon';
import TaskDueDate from '../../components/shared/TaskDueDate';
import TaskNotifications from '../../components/shared/TaskNotifications';
import ClickableFeatureTag from '../../components/shared/ClickableFeatureTag';
import ManagerDiscussionMarker from '../../components/shared/ManagerDiscussionMarker';
import { Tooltip } from '../../components/ui/Tooltip';
import { FeatherArrowRight, FeatherFileSmall, FeatherEdit2 } from '../../components/ui/icons';
import api from '../../services/api';
import { groupByStatus, getStatusColumnOrder } from './hubBoardUtils';
import './HubBoardView.css';

// Format task status for display (e.g. "in-progress" -> "In Progress")
const formatTaskStatusLabel = (status) => {
  if (!status) return 'Open';
  const s = String(status).toLowerCase();
  if (s === 'in-progress') return 'In Progress';
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');
};

const getStatusVariant = (status) => {
  switch (status?.toLowerCase()) {
    case 'in-progress':
      return 'warning';
    case 'completed':
    case 'released':
      return 'success';
    case 'open':
      return 'neutral';
    default:
      return 'neutral';
  }
};

const HubBoardView = ({
  tasks,
  memberMap,
  onTaskClick,
  onStatusChange,
  getNotification,
  isTaskLoading,
  allFeatureTagsFromHub,
  allTasksFromHub,
  conversationCounts = {},
}) => {
  const [toast, setToast] = useState(null);
  const [optimisticUpdates, setOptimisticUpdates] = useState({});

  const showToast = useCallback((message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const grouped = groupByStatus(tasks);
  const columnOrder = getStatusColumnOrder(grouped);

  const handleStatusChange = useCallback(
    async (taskId, newStatus, memberId) => {
      const task = tasks.find((t) => t.id === taskId);
      if (!task || task.status === newStatus) return;
      if (newStatus === 'completed' || newStatus === 'released') return;

      setOptimisticUpdates((prev) => ({ ...prev, [taskId]: newStatus }));

      try {
        const { data } = await api.patch(`/api/tasks/${taskId}/status`, {
          status: newStatus,
        });
        setOptimisticUpdates((prev) => {
          const next = { ...prev };
          delete next[taskId];
          return next;
        });
        onStatusChange?.(taskId, data, memberId);
      } catch (err) {
        setOptimisticUpdates((prev) => {
          const next = { ...prev };
          delete next[taskId];
          return next;
        });
        showToast('Failed to update task status');
      }
    },
    [tasks, onStatusChange]
  );

  const handleDragEnd = useCallback(
    (result) => {
      const { source, destination } = result;
      if (!destination || source.droppableId === destination.droppableId) return;

      const taskId = result.draggableId;
      const newStatus = destination.droppableId;
      const task = tasks.find((t) => t.id === taskId);
      const memberId = task?.memberId;

      handleStatusChange(taskId, newStatus, memberId);
    },
    [tasks, handleStatusChange]
  );

  const getEffectiveStatus = (task) =>
    optimisticUpdates[task.id] ?? task.status ?? 'in-progress';

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="hub-board-view">
        <div className="hub-board-columns">
        {columnOrder.map((statusKey) => {
          const columnTasks = (grouped[statusKey] || []).map((t) => ({
            ...t,
            memberId: t.memberId,
          }));
          const count = columnTasks.length;

          const isDropDisabled = statusKey === 'completed' || statusKey === 'released';

          return (
            <div key={statusKey} className={`hub-board-column ${isDropDisabled ? 'hub-board-column-readonly' : ''}`}>
              <div className="hub-board-column-header">
                <span className="hub-board-column-title">
                  {formatTaskStatusLabel(statusKey)}
                </span>
                <span className="hub-board-column-count">({count})</span>
              </div>
              <Droppable droppableId={statusKey} isDropDisabled={isDropDisabled}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="hub-board-column-content"
                  >
                    {columnTasks.length === 0 ? (
                      <div className="hub-board-column-empty">No tasks</div>
                    ) : (
                      columnTasks.map((task, index) => (
                        <Draggable
                          key={task.id}
                          draggableId={String(task.id)}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`hub-board-card ${snapshot.isDragging ? 'hub-board-card-dragging' : ''}`}
                              onClick={() => onTaskClick(task, task.memberId)}
                            >
                              <div className="hub-board-card-header">
                                <TaskTypeIcon taskType={task.task_type} />
                                <span className="hub-board-card-title">
                                  {task.display_id && (
                                    <span className="hub-board-card-display-id">
                                      [{task.display_id}]
                                    </span>
                                  )}
                                  {task.title}
                                </span>
                                <Tooltip content="View Task Summary">
                                  <button
                                    className="hub-board-card-summary-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onTaskClick(task, task.memberId);
                                    }}
                                    aria-label="View task summary"
                                  >
                                    <FeatherArrowRight size={16} />
                                  </button>
                                </Tooltip>
                              </div>
                              <div className="hub-board-card-meta">
                                {getNotification && (
                                  <TaskNotifications
                                    taskId={task.id}
                                    taskTitle={task.title}
                                    notification={getNotification(task.id)}
                                    isLoading={isTaskLoading?.(task.id)}
                                  />
                                )}
                                <ManagerDiscussionMarker count={conversationCounts[task.id]} />
                                {task.memberId && memberMap?.[task.memberId] && (
                                  <div className="hub-board-card-assignee">
                                    <Avatar size="small">
                                      {memberMap[task.memberId]?.name?.charAt(0) || '?'}
                                    </Avatar>
                                    <span>{memberMap[task.memberId].name}</span>
                                  </div>
                                )}
                                <TaskDueDate
                                  dueDate={task.feature_tag?.due_date}
                                  taskStatus={getEffectiveStatus(task)}
                                />
                                {task.work_plan_attachments?.length > 0 && (
                                  <Tooltip
                                    content={`${task.work_plan_attachments.length} attachment${task.work_plan_attachments.length !== 1 ? 's' : ''}`}
                                  >
                                    <span className="hub-board-card-indicator">
                                      <FeatherFileSmall size={10} />
                                    </span>
                                  </Tooltip>
                                )}
                                {task.task_pull_requests?.length > 0 && (
                                  <Tooltip
                                    content={`${task.task_pull_requests.length} PR${task.task_pull_requests.length !== 1 ? 's' : ''}`}
                                  >
                                    <span className="hub-board-card-indicator">
                                      <FeatherEdit2 size={10} />
                                    </span>
                                  </Tooltip>
                                )}
                                <Badge
                                  variant={getStatusVariant(getEffectiveStatus(task))}
                                  className="hub-board-card-status"
                                >
                                  {formatTaskStatusLabel(getEffectiveStatus(task))}
                                </Badge>
                                <Badge variant="neutral" className="hub-board-card-hours">
                                  {task.hours || '0'}h
                                </Badge>
                              </div>
                              {(task.project?.name || task.feature_tag) && (
                                <div className="hub-board-card-footer">
                                  {task.project?.name && (
                                    <ProjectBadge projectName={task.project.name} />
                                  )}
                                  {task.feature_tag && (
                                    <ClickableFeatureTag
                                      featureTag={task.feature_tag}
                                      allFeatureTags={allFeatureTagsFromHub || []}
                                      allTasks={allTasksFromHub || []}
                                      size="small"
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>

        {toast && (
          <div
            className={`hub-board-toast hub-board-toast-${toast.type}`}
            role="alert"
          >
            {toast.message}
          </div>
        )}
      </div>
    </DragDropContext>
  );
};

export default HubBoardView;
